import { useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSubscriptionWithReconnect } from "./useSubscriptionWithReconnect";
import { RootState, AppDispatch } from "../store";
import { selectConversation } from "../store/aiChatSlice";

// GraphQL subscription for all messages (no filter - auth rules handle it)
const onCreateMCPMessageAll = /* GraphQL */ `
  subscription OnCreateMCPMessageAll {
    onCreateMCPMessageAll {
      id
      conversationId
      content
      isUser
      status
      sessionId
      model
      modelCodename
      executionTimeMs
      toolsCount
      rounds
      serverId
      inputTokens
      outputTokens
      messageRead
      owner
      organizationId
      createdAt
      updatedAt
    }
  }
`;

// Subscription for message UPDATES — resolves a "pending" placeholder to the
// answer ("complete") or "error". Selection set must match what the Lambda's
// updateMCPMessage mutation returns, or fields arrive null.
const onUpdateMCPMessageAll = /* GraphQL */ `
  subscription OnUpdateMCPMessageAll {
    onUpdateMCPMessageAll {
      id
      conversationId
      content
      isUser
      status
      sessionId
      model
      modelCodename
      executionTimeMs
      toolsCount
      rounds
      serverId
      inputTokens
      outputTokens
      messageRead
      rating
      ratingComment
      owner
      organizationId
      createdAt
      updatedAt
    }
  }
`;

export interface MCPMessage {
  id: string;
  conversationId: string;
  content: string;
  isUser: boolean;
  status?: "pending" | "complete" | "error";
  sessionId?: string;
  model?: string;
  modelCodename?: string;
  executionTimeMs?: number;
  toolsCount?: number;
  rounds?: number;
  serverId?: string;
  inputTokens?: number;
  outputTokens?: number;
  messageRead?: boolean;
  rating?: "UP" | "DOWN" | null;
  ratingComment?: string | null;
  owner?: string;
  organizationId?: string;
  createdAt: string;
  updatedAt: string;
}

// Type for subscription response data
interface SubscriptionData {
  onCreateMCPMessageAll: MCPMessage | null;
}

interface UpdateSubscriptionData {
  onUpdateMCPMessageAll: MCPMessage | null;
}

interface UseAllMessagesSubscriptionResult {
  reconnect: () => void;
}

/**
 * Hook to subscribe to ALL new messages across all conversations.
 * AppSync auth rules automatically filter to only show messages the user is authorized to see.
 *
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Refetch current conversation on reconnection
 * - Connection status tracking via Redux
 *
 * @param onNewMessage - Callback invoked when a new AI message arrives (for any conversation)
 * @param onUpdateMessage - Callback invoked when an AI message is updated (placeholder resolved)
 * @returns Object with reconnect function for manual retry
 */
export function useAllMessagesSubscription(
  onNewMessage: (message: MCPMessage) => void,
  onReconnected?: () => void,
  onUpdateMessage?: (message: MCPMessage) => void
): UseAllMessagesSubscriptionResult {
  const dispatch = useDispatch<AppDispatch>();
  const currentConversation = useSelector(
    (state: RootState) => state.aiChat.currentConversation
  );
  const connectionStatus = useSelector(
    (state: RootState) => state.subscription.connectionStatus
  );

  // Keep refs to avoid re-subscriptions
  const onNewMessageRef = useRef(onNewMessage);
  const onReconnectedRef = useRef(onReconnected);
  const onUpdateMessageRef = useRef(onUpdateMessage);
  const wasDisconnectedRef = useRef(false);
  const currentConversationIdRef = useRef(currentConversation?.id);

  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
  }, [onNewMessage]);

  useEffect(() => {
    onReconnectedRef.current = onReconnected;
  }, [onReconnected]);

  useEffect(() => {
    onUpdateMessageRef.current = onUpdateMessage;
  }, [onUpdateMessage]);

  useEffect(() => {
    currentConversationIdRef.current = currentConversation?.id;
  }, [currentConversation?.id]);

  // Track disconnection for refetch logic
  useEffect(() => {
    if (
      connectionStatus === "disconnected" ||
      connectionStatus === "reconnecting"
    ) {
      wasDisconnectedRef.current = true;
    }
  }, [connectionStatus]);

  // Refetch conversation on reconnection to catch missed messages
  const handleReconnected = useCallback(() => {
    if (!wasDisconnectedRef.current) return;
    wasDisconnectedRef.current = false;

    if (onReconnectedRef.current) {
      // Caller provides their own reconnect logic (e.g. project chat)
      onReconnectedRef.current();
    } else if (currentConversationIdRef.current) {
      // Default: refetch the regular aiChat conversation
      dispatch(selectConversation(currentConversationIdRef.current));
    }
  }, [dispatch]);

  // Handle incoming subscription data
  const handleData = useCallback((data: SubscriptionData) => {
    const newMessage = data?.onCreateMCPMessageAll;

    // Only process AI messages (user messages are added locally)
    if (newMessage && !newMessage.isUser) {
      onNewMessageRef.current(newMessage);
    }
  }, []);

  // Handle message updates (placeholder → complete/error)
  const handleUpdateData = useCallback((data: UpdateSubscriptionData) => {
    const updatedMessage = data?.onUpdateMCPMessageAll;
    if (updatedMessage && !updatedMessage.isUser) {
      onUpdateMessageRef.current?.(updatedMessage);
    }
  }, []);

  const { reconnect } = useSubscriptionWithReconnect<SubscriptionData>({
    query: onCreateMCPMessageAll,
    onData: handleData,
    onReconnected: handleReconnected,
  });

  // Separate subscription for updates. Reconnect-driven refetch is handled by the
  // create subscription above (it refetches the conversation, which carries the
  // latest message status), so this one doesn't need its own onReconnected.
  useSubscriptionWithReconnect<UpdateSubscriptionData>({
    query: onUpdateMCPMessageAll,
    onData: handleUpdateData,
    enabled: !!onUpdateMessage,
  });

  return { reconnect };
}
